import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { SzButton, SzInput, SzDatePicker } from "@suezenv/react-theme-components";
import { dateToString, FORMAT_DATE } from "../../../helpers/date.util";
import { useTranslationWithPrefix } from "../../../hooks/translation";
import { OptionItem } from "../../../form/SzMultipleCheckbox/SzMultipleCheckbox";
import "./signal-filter.form.scss";
import { RequestTypeReference, WorkflowStatusReference } from "../../../type/organization.type";
import { ISignalAdvancedFavoriteFilters, ISignalFilter, SignalFilterStatus } from "../../../type/signal.type";
import { GeographicalLatLng, SearchAddressFilters } from "../../../type/Address.type";
import { DEFAULT_STATUS_FILTER, DEFAULT_RADIUS, ADVANCED_FILTERS_TYPES, DEFAULT_START_DATE, DEFAULT_END_DATE } from "../../../constants/filters";
import { STATUS } from "../../../constants/status";
import {SignalAdvancedFilter} from "./AdvancedFilter/signal-advanced-filter";

interface Props {
    requestTypes: RequestTypeReference[];
    status: WorkflowStatusReference[];
    appliedFilters: ISignalFilter;
    onSubmit: (values: ISignalFilter) => any;
    searchAddressFilters?: SearchAddressFilters;
    geographicalPriority?: GeographicalLatLng;
    addSignal: () => void;
    favoriteFiltersList: ISignalAdvancedFavoriteFilters[];
    setFavoriteFiltersList: (favoriteFiltersList: ISignalAdvancedFavoriteFilters[]) => void;
    downloadExport?: () => void;
}

const filterStatus: SignalFilterStatus = {
    status: DEFAULT_STATUS_FILTER
};

const SignalFilterForm: React.FC<Props> = (props) => {
    const {
        onSubmit,
        appliedFilters,
        addSignal,
        geographicalPriority,
        searchAddressFilters,
        favoriteFiltersList,
        setFavoriteFiltersList,
        downloadExport,
    } = props;
    const trans = useTranslationWithPrefix("signalement", "form_filter");
    const [search, setSearch] = useState<string>(appliedFilters.q || "");
    const [loader/*, setLoader*/] = useState(false);
    const [dateError, setDateError] = useState<any>(null);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [countActiveFilters, setCountActiveFilters] = useState(0);
    const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState<ISignalAdvancedFavoriteFilters | null>(null);
    const [startDate, setStartDate] = useState(new Date(appliedFilters.startDate || DEFAULT_START_DATE));
    const [endDate, setEndDate] = useState(new Date(appliedFilters.endDate || DEFAULT_END_DATE));
    const [defaultFilters, setDefaultFilters] = useState<ISignalFilter>();
    

    const typesOptions: OptionItem[] = useMemo(() => {
        return props.requestTypes.map((state: RequestTypeReference) => {
            return { label: state.label, value: state.requestType };
        });
    }, [props.requestTypes]);

    const statusOptions = useMemo(() => {
        return props.status.map((state: WorkflowStatusReference) => {
            return { label: state.label, value: state.statusId };
        });
    }, [props.status]);

    const toggleFilter = (): any => {
        setShowFilters(!showFilters);
    }


    useEffect(() => {
        if (statusOptions.length > 0 && typesOptions.length > 0) {
            setDefaultFilters({
                status: statusOptions.filter((statusOption) =>
                    filterStatus.status.map((item) => item.value).includes(statusOption.value)
                    && statusOption.value !== STATUS.CLOSED,
                ),
                requestTypes: typesOptions,
            });
        }
    }, [statusOptions, typesOptions]);

    useEffect(() => {
        let countActiveFilters = 0;
        Object.entries(appliedFilters).forEach(([key, filter]: [string, any]) => {
            const isArrayWithLength = Array.isArray(filter) && filter.length > 0;
            const isStringWithLength = typeof filter === "string" && filter.trim().length > 0;
            const isObjectWithKeys = typeof filter === "object" && filter !== null && Object.keys(filter).length > 0;
            const isNumberFilter = typeof filter === "number" && !isNaN(filter);

            if ((isArrayWithLength || isObjectWithKeys || isNumberFilter || isStringWithLength)
                && Object.prototype.hasOwnProperty.call(availableFilters, key)
            ) {
                countActiveFilters++;
            }
        });
        setCountActiveFilters(countActiveFilters);
    }, [appliedFilters]);


    const signalmentFilterInit: ISignalFilter = useMemo(() => {
        let fromOneYear = new Date().setFullYear(new Date().getFullYear() - 1);
        let inOneYear = new Date().setFullYear(new Date().getFullYear() + 1);

        return {
            status: ("status" in appliedFilters)
                ? appliedFilters.status
                : statusOptions.filter((statusOption) =>
                    filterStatus.status.map((item) => item.value).includes(statusOption.value)
                    && statusOption.value !== STATUS.CLOSED,
            ),
            requestTypes: ("requestTypes" in appliedFilters) ? appliedFilters.requestTypes : typesOptions,
            startDate: ("startDate" in appliedFilters) ? appliedFilters.startDate : dateToString(new Date(fromOneYear), FORMAT_DATE),
            endDate: ("endDate" in appliedFilters) ? appliedFilters.endDate : dateToString(new Date(inOneYear), FORMAT_DATE),
            address: ("address" in appliedFilters) ? appliedFilters.address : undefined,
            radius: ("radius" in appliedFilters) ? appliedFilters.radius : undefined,
            instigators: [],
            q: ("q" in appliedFilters) ? appliedFilters.q : "",
        };
    }, [appliedFilters, typesOptions, statusOptions]);

    const availableFilters = {
        [ADVANCED_FILTERS_TYPES.STATUS]: {
            label: 'Statuts',
            type: 'select',
            defaultValue: Array.isArray(appliedFilters.status) && appliedFilters.status.length > 0
                ? appliedFilters.status
                : statusOptions.filter((statusOption) =>
                    filterStatus.status.map((item) => item.value).includes(statusOption.value)
                    && statusOption.value !== STATUS.CLOSED,
            ),
            options: statusOptions,
            isMulti: true,
            isMultiList: true,
            isClearable: true
        },
        [ADVANCED_FILTERS_TYPES.REQUEST_TYPES]: {
            label: 'Types de signalement',
            type: 'select',
            defaultValue: Array.isArray(appliedFilters.requestTypes) && appliedFilters.requestTypes?.length > 0
                ? appliedFilters.requestTypes
                : typesOptions,
            options: typesOptions,
            isMulti: true,
            isMultiList: true,
            isClearable: true
        },
        [ADVANCED_FILTERS_TYPES.ADDRESS]: {
            label: 'Adresse',
            type: 'address',
            defaultValue: appliedFilters.address?.address ? appliedFilters.address.address : appliedFilters.address ? appliedFilters.address : '',
            geographicalPriority: geographicalPriority,
            searchAddressFilters: searchAddressFilters
        },
        [ADVANCED_FILTERS_TYPES.RADIUS]: {
            label: 'Rayon (en mètres)',
            type: 'input',
            defaultValue: appliedFilters.radius ? appliedFilters.radius : DEFAULT_RADIUS
        }
    };

    const handleFormSubmit = (values: ISignalFilter) => {
        // Apply default status in filter if they are not already set
        if (statusOptions.length > 0 && (values.status?.length === 0) || !values.status) {
            values.status = statusOptions.filter((statusOption) =>
                filterStatus.status.map((item) => item.value).includes(statusOption.value)
                && statusOption.value !== STATUS.CLOSED,
            );
        }
        // Apply default requestTypes in filter if they are not already set
        if (typesOptions.length > 0 && (values.requestTypes?.length === 0) || !values.requestTypes) {
            values.requestTypes = typesOptions;
        }

        const orderedFilters = {
            status: values.status,
            requestTypes: values.requestTypes,
            ...values 
        };

        onSubmit(orderedFilters);
        setShowFilters(false);
    };

    const onChangeStartDate = (startDate: Date) => {
        // This test prevent click on date and manually change value, this run event SyntheticEvent
        setDateError(null)
        if (startDate instanceof Date) {
            if (startDate > endDate) {
                setDateError(<div className="invalid-feedback"
                    style={{ display: 'block' }}>{trans('not_valid_daterange_picker')}</div>)
            }
            setStartDate(startDate);

            let newFiltersToSet: ISignalFilter = {
                ...appliedFilters,
                startDate: dateToString(startDate, FORMAT_DATE)
            }
            onSubmit(newFiltersToSet);
        }
    }

    const onChangeEndDate = (endDate: Date) => {
        setDateError(null)
        if (endDate instanceof Date) {
            if (startDate > endDate) {
                setDateError(<div className="invalid-feedback"
                                  style={{display: 'block'}}>{trans('not_valid_daterange_picker')}</div>)
            }
            setEndDate(endDate);

            let newFiltersToSet: ISignalFilter = {
                ...appliedFilters,
                endDate: dateToString(endDate, FORMAT_DATE)
            }
            onSubmit(newFiltersToSet);
        }
    }

    return (
        <div className='m-3 p-3 row signal-filter signal-header signal-advanced-filter'>
            <div className='col-12'>
                <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={signalmentFilterInit}
                    enableReinitialize={true}
                    validateOnChange={false}
                >
                    {({ handleSubmit, handleReset, values, setFieldValue }) => {
                        return (
                            <Form onSubmit={handleSubmit} onReset={handleReset}>
                                <div className='form-row d-flex'>
                                    <div className='col-sm-3'>
                                        <SzInput
                                            className='border'
                                            onChange={({ target }) => {
                                                setSearch(target.value)
                                                setFieldValue('q', target.value)
                                            }}
                                            placeholder={trans('action.search')}
                                            value={search}
                                            handleClear={() => {
                                                setFieldValue('q', '')
                                                setSearch('')
                                                handleSubmit();
                                            }}
                                        />
                                    </div>
                                    <div className='d-flex'>
                                        <SzButton
                                            className='filter-search-submit pl-2 pr-0'
                                            icon='search'
                                            type="submit"
                                            isDisabled={loader}
                                            loader={loader}
                                        >
                                            <span className='text-hide'>{trans('ok')}</span>
                                        </SzButton>
                                    </div>
                                    <div className="d-flex ml-2 date-range">
                                        <SzDatePicker
                                            startDate={startDate}
                                            onChangeStartDate={onChangeStartDate}
                                            endDate={endDate}
                                            onChangeEndDate={onChangeEndDate}
                                            dateRange
                                            classNameStartDate="pl-3"
                                            classNameEndDate="pr-3"
                                        />
                                        {dateError}
                                    </div>
                                    <div className='d-flex position-relative'>
                                        <SzButton
                                            className='request-filter ml-2 z-base pl-2'
                                            icon='filter-1'
                                            onClick={toggleFilter}
                                            variant='secondary'
                                        >
                                            <span>{trans('filter_button')}
                                                {countActiveFilters > 0 && " (" + countActiveFilters + ")"}
                                            </span>
                                        </SzButton>
                                        {showFilters && (
                                            <SignalAdvancedFilter
                                                filters={appliedFilters}
                                                defaultFilters={defaultFilters}
                                                externalFilters={availableFilters}
                                                setFieldValue={setFieldValue}
                                                onClose={toggleFilter}
                                                favoriteFiltersList={favoriteFiltersList}
                                                setFavoriteFiltersList={setFavoriteFiltersList}
                                                formValues={values}
                                                selectedFavoriteFilter={selectedFavoriteFilter}
                                                setSelectedFavoriteFilter={setSelectedFavoriteFilter}
                                            />
                                        )}
                                    </div>
                                    {downloadExport && (<div className='d-flex position-relative'>
                                        <SzButton
                                            className='request-filter ml-2 z-base pl-2'
                                            icon='download-bottom'
                                            onClick={downloadExport}
                                            variant='secondary'
                                        >
                                            <span>{trans('action.exported')}</span>
                                        </SzButton>
                                    </div>)}
                                    <div className="d-flex ml-auto">
                                        <SzButton
                                            icon='add-square'
                                            alignIcon='right'
                                            className='add-request float-right'
                                            onClick={addSignal}
                                        >
                                            {trans('action.add')}
                                        </SzButton>
                                    </div>

                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};

export default SignalFilterForm;
